import { useMemo, useState } from 'react';
import { X, Minus, ArrowsOutSimple } from '@phosphor-icons/react';
import { aircraftType } from '../data/aircraft';
import { airportById } from '../data/airports';
import { managerNetAssets } from '../game/auction';
import type { GameState } from '../game/types';
import { money, integer } from '../ui/format';
import { FormattedNumberInput } from '../ui/FormattedNumberInput';
import { useI18n } from '../i18n';

const INCREMENTS = [1, 2, 3, 4, 5];

interface Props {
  game: GameState;
  onBid: (incrementPercent: number) => void;
  onFixedBid: (amount: number) => void;
  onAbandon: () => void;
  onMinimize: () => void;
}

export function AuctionModal({ game, onBid, onFixedBid, onAbandon, onMinimize }: Props) {
  const { t } = useI18n();
  const auction = game.activeAuction;
  const [selectedIncrement, setSelectedIncrement] = useState(2);
  const [useCustomBid, setUseCustomBid] = useState(false);
  const [customBid, setCustomBid] = useState('');

  const target = useMemo(() => game.aiManagers.find((m) => m.id === auction?.targetManagerId), [game.aiManagers, auction?.targetManagerId]);

  if (!auction || !target) return null;

  const netAssets = managerNetAssets(target);
  const debt = (target.loans ?? []).reduce((sum, loan) => sum + loan.principalRemaining, 0);
  const minIncrement = Math.max(1, Math.round(netAssets * 0.01));
  const minBid = auction.currentPrice + minIncrement;

  const customBidAmount = Math.floor(Number(customBid) || 0);
  const myNextBid = useCustomBid
    ? Math.max(minBid, customBidAmount)
    : Math.round(auction.currentPrice + Math.max(1, netAssets * (selectedIncrement / 100)));
  const canAfford = game.cash >= myNextBid;
  const customBidValid = !useCustomBid || customBidAmount > 0;

  const winnerName = auction.currentWinnerId === 'player' ? t('ui.你') : game.aiManagers.find((m) => m.id === auction.currentWinnerId)?.name ?? '-';

  return (
    <div className="modal-backdrop auction-backdrop" role="presentation">
      <section className="auction-sheet" role="dialog" aria-modal="true" aria-labelledby="auction-title">
        <header className="auction-header">
          <div>
            <span className="eyebrow">{t(auction.kind === 'acquisition' ? 'ui.收购要约竞拍' : 'ui.破产收购竞拍')}</span>
            <h2 id="auction-title">{target.name} {t(auction.kind === 'acquisition' ? 'ui.航司收购' : 'ui.破产清算')}</h2>
            <p>{t('ui.第 {round} 轮 · 起拍价 {price}', { round: auction.round, price: money(Math.round(netAssets * 0.1), false) })}</p>
          </div>
          <div className="auction-window-controls">
            <button className="icon-button" onClick={onMinimize} aria-label={t('ui.最小化')} title={t('ui.最小化')}><Minus weight="bold" size={18} /></button>
            <button className="icon-button" onClick={onAbandon} aria-label={t('ui.关闭并放弃')} title={t('ui.关闭并放弃')}><X weight="bold" size={18} /></button>
          </div>
        </header>

        <div className="auction-body">
          <div className="auction-panel">
            <h3>{t('ui.标的概况')}</h3>
            <div className="auction-kpi-grid">
              <div><span>{t('ui.净资产')}</span><strong>{money(netAssets, false)}</strong></div>
              <div><span>{t('ui.负债')}</span><strong className="red">−{money(debt, false)}</strong></div>
              <div><span>{t('ui.现金')}</span><strong>{money(target.cash, false)}</strong></div>
              <div><span>{t('ui.飞机')}</span><strong>{integer(target.aircraft.length)} {t('ui.架')}</strong></div>
              <div><span>{t('ui.航线')}</span><strong>{integer(target.routes.length)} {t('ui.条')}</strong></div>
              <div><span>{t('ui.枢纽')}</span><strong>{integer(target.hubs.length)} {t('ui.个')}</strong></div>
            </div>

            <h4>{t('ui.飞机清单')}</h4>
            <ul className="auction-aircraft-list">
              {target.aircraft.length === 0 && <li>{t('ui.无飞机')}</li>}
              {target.aircraft.map((a) => {
                const type = aircraftType(a.typeId);
                return (
                  <li key={a.id}>
                    <strong>{type.name}</strong>
                    <span>机况 {(a.condition * 100).toFixed(0)}% · 账面 {money(a.bookValue, false)}</span>
                  </li>
                );
              })}
            </ul>

            <h4>{t('ui.航线网络')}</h4>
            <ul className="auction-route-list">
              {target.routes.length === 0 && <li>{t('ui.无航线')}</li>}
              {target.routes.slice(0, 8).map((route) => {
                const origin = airportById(route.originId);
                const destination = airportById(route.destinationId);
                return (
                  <li key={route.id}>
                    {origin.nameZh} ↔ {destination.nameZh}
                    <span>{route.originId}-{route.destinationId} · {route.active ? '运营中' : '已停飞'}</span>
                  </li>
                );
              })}
              {target.routes.length > 8 && <li className="auction-more">还有 {target.routes.length - 8} 条航线</li>}
            </ul>
          </div>

          <div className="auction-panel">
            <h3>出价历史</h3>
            {auction.bids.length === 0 ? (
              <p className="auction-empty">暂无人出价，起拍价 {money(auction.currentPrice, false)}</p>
            ) : (
              <ol className="auction-bid-list">
                {auction.bids.map((bid, index) => (
                  <li key={index} className={bid.bidderId === 'player' ? 'mine' : ''}>
                    <span>{bid.bidderId === 'player' ? '你' : game.aiManagers.find((m) => m.id === bid.bidderId)?.name ?? '未知'}</span>
                    <strong>{money(bid.amount, false)}</strong>
                  </li>
                ))}
              </ol>
            )}

            <div className="auction-current">
              <span>当前最高价</span>
              <strong>{money(auction.currentPrice, false)}</strong>
              <small>领先：{winnerName}</small>
            </div>
          </div>

          <div className="auction-panel auction-action-panel">
            <h3>你的出价</h3>
            <div className="auction-cash">
              <span>可用现金</span>
              <strong>{money(game.cash, false)}</strong>
            </div>
            <div className="auction-my-bid">
              <span>预计出价</span>
              <strong className={canAfford ? 'green' : 'red'}>{money(myNextBid, false)}</strong>
            </div>

            <div className="auction-increment-selector">
              <div className="auction-bid-mode-toggle">
                <button
                  type="button"
                  className={!useCustomBid ? 'active' : ''}
                  onClick={() => setUseCustomBid(false)}
                >
                  快捷加价
                </button>
                <button
                  type="button"
                  className={useCustomBid ? 'active' : ''}
                  onClick={() => setUseCustomBid(true)}
                >
                  输入目标价
                </button>
              </div>
              {!useCustomBid ? (
                <>
                  <span>加价幅度（相对净资产）</span>
                  <div className="auction-increment-buttons">
                    {INCREMENTS.map((inc) => (
                      <button
                        key={inc}
                        className={selectedIncrement === inc ? 'active' : ''}
                        onClick={() => setSelectedIncrement(inc)}
                      >
                        +{inc}%
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className="auction-custom-bid">
                  <span>目标价（不得低于 {money(minBid, false)}）</span>
                  <FormattedNumberInput
                    value={customBid}
                    onChange={(value) => setCustomBid(value)}
                    min={1}
                    max={game.cash}
                    aria-label="目标出价"
                  />
                </div>
              )}
            </div>

            {!canAfford && (
              <p className="auction-warning">现金不足，可最小化窗口去财务中心融资。</p>
            )}

            <div className="auction-actions">
              <button
                className="primary-button"
                disabled={!canAfford || !customBidValid}
                onClick={() => (useCustomBid ? onFixedBid(customBidAmount) : onBid(selectedIncrement))}
              >
                出价 {money(myNextBid, false)}
              </button>
              <button className="secondary-action" onClick={onAbandon}>放弃竞拍</button>
              <button className="secondary-action" onClick={onMinimize}>最小化</button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export function AuctionMinimizedFab({ game, onRestore }: { game: GameState; onRestore: () => void }) {
  const auction = game.activeAuction;
  if (!auction || !auction.minimized) return null;
  const target = game.aiManagers.find((m) => m.id === auction.targetManagerId);
  if (!target) return null;

  return (
    <button className="auction-fab" onClick={onRestore}>
      <ArrowsOutSimple weight="bold" size={16} />
      <span>{target.name} 竞拍中 · 当前 {money(auction.currentPrice, false)}</span>
    </button>
  );
}
